import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';
import { MantineProvider } from '@mantine/core';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import { Notifications } from '@mantine/notifications';
import '@mantine/notifications/styles.css';
import { Refine } from '@refinedev/core';
import routerProvider from '@refinedev/react-router-v6';
import { ErrorBoundary } from '@sentry/react';
import 'mantine-datatable/styles.layer.css';
import { BrowserRouter } from 'react-router-dom';
import { MutationCache, QueryClient } from '@tanstack/react-query';
import AllRoutes from './AllRoutes';
import { createMantineTheme, cssVariableResolver } from './UI/theme/theme';
import { GenericErrorPage } from './error/GenericErrorPage';
import {
  DeletionIntegrityErrorProvider,
  useDeletionIntegrityError,
} from './error/DeletionIntegrityErrorProvider';
import { ApiError, ApiErrorCode } from './error/ApiError';
import { FeatureFlagProvider } from './feature-flags/FeatureFlagProvider';
import { authProvider } from './providers/authProvider';
import { dataProvider } from './providers/dataProvider';
import { defaultQueryFn } from 'src/common/utils/api';
import { PageViewTracker } from './tracking/PageViewTracker';
import { SessionExpirationMonitor } from './auth/SessionExpirationMonitor';
import { MaintenanceGate } from './maintenance/MaintenanceGate';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useRef } from 'react';

dayjs.extend(customParseFormat);

function AppContent() {
  const { showError } = useDeletionIntegrityError();
  const queryClientRef = useRef<QueryClient | null>(null);

  // Create QueryClient with global error handling for integrity errors
  if (!queryClientRef.current) {
    queryClientRef.current = new QueryClient({
      mutationCache: new MutationCache({
        onError: error => {
          // Check if it's an integrity error
          if (error instanceof ApiError && error.code === ApiErrorCode.ENTITY_HAS_DEPENDENCIES) {
            showError(error.message);
          }
        },
      }),
      defaultOptions: {
        queries: {
          retry: (failureCount: number, error: unknown) => {
            if (error instanceof Response) {
              if (error.status === 401 || error.status === 403) {
                return false;
              }
            }

            return failureCount < 3;
          },
          queryFn: defaultQueryFn,
        },
      },
    });
  }

  return (
    <BrowserRouter>
      <ErrorBoundary fallback={({ error }) => <GenericErrorPage error={error} />}>
        <Refine
          dataProvider={dataProvider}
          authProvider={authProvider}
          routerProvider={routerProvider}
          options={{
            reactQuery: {
              clientConfig: queryClientRef.current,
            },
          }}
        >
          <PageViewTracker />
          <SessionExpirationMonitor />
          <MaintenanceGate>
            <AllRoutes />
          </MaintenanceGate>
        </Refine>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

function App() {
  const theme = createMantineTheme();

  return (
    <MantineProvider theme={theme} cssVariablesResolver={cssVariableResolver}>
      <Notifications />
      <DeletionIntegrityErrorProvider>
        <AppContent />
      </DeletionIntegrityErrorProvider>
    </MantineProvider>
  );
}

export default function AppWithFlags() {
  return (
    <FeatureFlagProvider>
      <App />
    </FeatureFlagProvider>
  );
}
